import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaHome, FaInfoCircle, FaPhone, FaStar, FaQuestionCircle, FaTag, FaTelegramPlane, FaBars, FaTimes, FaCircle } from "react-icons/fa";
import Logo from "./logo";
import { useSocial } from "../hooks/useSocial";

const LIST = [
  { text: "Home", link: "/", icon: <FaHome className="text-xs" /> },
  { text: "About", link: "#about", icon: <FaInfoCircle className="text-xs" /> },
  { text: "Benefits", link: "#benefits", icon: <FaStar className="text-xs" /> },
  // { text: "Pricing", link: "#pricing", icon: <FaTag className="text-xs" /> },
  { text: "Reviews", link: "#reviews", icon: <FaStar className="text-xs" /> },
  { text: "FAQ", link: "#faq", icon: <FaQuestionCircle className="text-xs" /> },
  { text: "Contact", link: "#contact", icon: <FaPhone className="text-xs" /> },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { socials } = useSocial();
  const telegram = socials.find((item) => item.label?.toLowerCase() === "telegram");
  // console.log(telegram)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        scrolled
          ? "bg-[#050a14]/95 backdrop-blur-xl border-b border-blue-500/10 shadow-[0_4px_30px_rgba(59,130,246,0.1)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
        <Logo />

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          <ul className="flex items-center gap-6">
            {LIST.map((item, i) => (
              <motion.li
                key={i}
                whileHover={{ y: -2 }}
                className="relative group"
              >
                <a
                  href={item.link}
                  className="text-slate-400 hover:text-white text-sm font-medium tracking-wide transition-colors duration-300 font-exo flex items-center gap-1.5"
                >
                  <span className="opacity-60 group-hover:opacity-100 transition-opacity">{item.icon}</span>
                  {item.text}
                </a>
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-gradient-to-r from-green-400 to-blue-500 group-hover:w-full transition-all duration-300" />
              </motion.li>
            ))}
          </ul>

          <motion.a
            href={telegram?.url || "#"}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative overflow-hidden group font-orbitron text-xs tracking-widest uppercase py-2.5 px-6 rounded-full text-white font-bold flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #22c55e, #3b82f6)",
              boxShadow: "0 0 20px rgba(34,197,94,0.3)",
            }}
          >
            <span className="relative z-10 flex items-center gap-2"><FaTelegramPlane /> Join Telegram</span>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: "linear-gradient(135deg, #3b82f6, #22c55e)" }}
            />
          </motion.a>
        </nav>

        {/* Mobile Menu Button */}
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          whileTap={{ scale: 0.9 }}
          className="lg:hidden text-white p-2"
        >
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {isOpen
              ? <FaTimes className="text-xl text-green-400" />
              : <FaBars className="text-xl text-green-400" />
            }
          </motion.div>
        </motion.button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0a1628]/98 backdrop-blur-xl border-t border-blue-500/10"
          >
            <ul className="px-6 py-6 flex flex-col gap-4">
              {LIST.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <a
                    href={item.link}
                    onClick={() => setIsOpen(false)}
                    className="text-slate-300 hover:text-green-400 text-base font-medium transition-colors flex items-center gap-3"
                  >
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>
                      {item.icon}
                    </span>
                    {item.text}
                  </a>
                </motion.li>
              ))}
              <li className="mt-2">
                <a
                  href={telegram?.url || "#"}
                  className="block text-center font-orbitron text-xs tracking-widest uppercase py-3 px-6 rounded-full text-white font-bold"
                  style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)" }}
                >
                  Join Telegram
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
